import { TONES, type CustomTone, type MenuSetting, type RewriteOptions } from '@lyrmo/shared';
import { useId, useState } from 'react';
import { Icon } from '../../components/Icon';
import { useWidthGlide } from '../../hooks/useWidthGlide';
import { useI18n } from '../../i18n/i18n-context';
import { cx } from '../../lib/cx';
import { matches, menuSections, toneItems, type MenuItem } from './menu-items';
import { MenuFooter, MenuRow, MenuSearch, MenuSection, type MenuAction } from './MenuPanel';
import { moveWithArrows, usePopover, useSearchFocus } from './usePopover';

type ToneOptions = Pick<RewriteOptions, 'tone' | 'customTone'>;

interface ToneMenuProps {
  readonly value: ToneOptions;
  readonly onChange: (value: ToneOptions) => void;
  /** The writer's own tones, listed after the built-in ones; null until they're known. */
  readonly customTones: readonly CustomTone[] | null;
  /** Which tones are hidden or pinned: as everyone starts without an account; null until known. */
  readonly settings?: readonly MenuSetting[] | null | undefined;
  /** Opens Manage tones; omitted where there's no account to keep them. */
  readonly onManage?: (() => void) | undefined;
  /** Starts a new tone of the writer's own; omitted where there's no account to keep it. */
  readonly onNew?: (() => void) | undefined;
}

/**
 * Tone, in the editor's toolbar: a chip naming the tone, which opens a menu of them, laid out
 * like AI tools: the pinned ones, then the built-in ones, then the writer's own, leaving out
 * those they hid, apart from the one chosen.
 */
export function ToneMenu({
  value,
  onChange,
  customTones,
  settings,
  onManage,
  onNew,
}: ToneMenuProps) {
  const { t } = useI18n();
  const { open, shown, present, leaving, setOpen, rootRef, triggerRef, onBlur } = usePopover();
  const searchRef = useSearchFocus(shown);
  const [query, setQuery] = useState('');
  const panelId = useId();
  const current = value.customTone ?? value.tone;
  const items = toneItems(customTones ?? [], settings, t);
  const sections = menuSections(items, (item) => item.id === current);
  const isSet = current !== TONES[0];
  // Until the writer's tones load, the chosen one is named only as a custom tone.
  const currentName =
    items.find((item) => item.id === current)?.name ??
    (value.customTone ? t.options.customTone : t.options.tones[value.tone]);
  useWidthGlide(triggerRef, isSet ? currentName : t.options.tone);

  /** Closes the menu for what's chosen in it. */
  const leave = (then: () => void) => () => {
    setOpen(false);
    then();
  };
  const choose = (item: MenuItem) =>
    leave(() => {
      // A custom tone's built-in tone goes back to the text's own, ready for when it's left.
      const tone = TONES.find((builtIn) => builtIn === item.id);
      onChange(tone ? { tone, customTone: undefined } : { tone: 'natural', customTone: item.id });
    });

  const found = (list: readonly MenuItem[]) => list.filter((item) => matches(item, query));
  const pinned = found(sections.pinned);
  const builtIn = found(sections.builtIn);
  const own = found(sections.own);
  const first = pinned[0] ?? builtIn[0] ?? own[0];
  const ownNote = query.trim()
    ? null
    : customTones === null
      ? t.tones.loading
      : customTones.length === 0
        ? t.tones.noneYet
        : null;

  const list = (choices: readonly MenuItem[]) => (
    <ul className="menu-list">
      {choices.map((item) => (
        <MenuRow key={item.id} item={item} current={item.id === current} onSelect={choose(item)} />
      ))}
    </ul>
  );

  const actions: MenuAction[] = [];
  if (onNew) {
    actions.push({ label: t.tones.newTone, icon: 'plus', onClick: leave(onNew), accent: true });
  }
  if (onManage) actions.push({ label: t.tones.title, icon: 'edit', onClick: leave(onManage) });

  return (
    <div className="tone-menu" ref={rootRef} onBlur={onBlur}>
      <button
        ref={triggerRef}
        type="button"
        className={cx('option option-button', isSet && 'is-set')}
        aria-label={t.options.tone}
        title={`${t.options.tone}: ${currentName}`}
        aria-haspopup="dialog"
        aria-expanded={shown}
        aria-controls={panelId}
        onClick={() => {
          if (!open) setQuery('');
          setOpen((current) => !current);
        }}
      >
        <span className="option-icon" aria-hidden="true">
          <Icon name="message" />
        </span>
        {/* Keyed by what it says, so a new choice slides in rather than swapping in place. */}
        <span key={isSet ? currentName : ''} className="option-value" aria-hidden="true">
          {isSet ? currentName : t.options.tone}
        </span>
        <span className="option-chevron" aria-hidden="true">
          <Icon name="chevron" />
        </span>
      </button>
      {present && (
        <div
          className={cx('menu-panel tone-panel', leaving && 'is-leaving')}
          id={panelId}
          inert={leaving}
          role="dialog"
          aria-label={t.options.tone}
          onKeyDown={moveWithArrows}
        >
          <div className="menu-top">
            <MenuSearch
              inputRef={searchRef}
              value={query}
              onChange={setQuery}
              onEnter={first && choose(first)}
            />
          </div>

          <div className="menu-body">
            {pinned.length > 0 && <MenuSection label={t.menus.pinned}>{list(pinned)}</MenuSection>}
            {builtIn.length > 0 && (
              <MenuSection label={t.menus.builtIn}>{list(builtIn)}</MenuSection>
            )}
            {(own.length > 0 || (onManage && ownNote)) && (
              <MenuSection label={t.options.yourTones}>
                {own.length > 0 && list(own)}
                {ownNote && (
                  <p className="menu-empty" role={customTones === null ? 'status' : undefined}>
                    {ownNote}
                  </p>
                )}
              </MenuSection>
            )}
            {query.trim() && !first && (
              <p className="menu-empty" role="status">
                {t.menus.noMatch(query.trim())}
              </p>
            )}
          </div>

          {actions.length > 0 && <MenuFooter hidden={sections.hidden} actions={actions} />}
        </div>
      )}
    </div>
  );
}
